import { ChangeDetectorRef, Directive, OnDestroy, OnInit } from '@angular/core';
import { Action, ComponentCollection, PanelModel, Question, QuestionCustomModel, Serializer } from 'survey-core';
import { ICreatorOptions, SurveyCreatorModel } from 'survey-creator-core';
import { v4 as uuidv4 } from 'uuid';

const allowedProps = ['visibleIf', 'enableIf', 'requiredIf', 'title', 'description', 'enableFlagForMaintenanceOption'];

const creatorOptions: ICreatorOptions = {
  showLogicTab: false,
  isAutoSave: true,
  showJSONEditorTab: true,
  questionTypes: ['checkbox', 'radiogroup', 'dropdown', 'boolean', 'comment', 'panel', 'file', 'propertyState'],
  allowChangeThemeInPreview: false,
  showSimulatorInTestSurveyTab: false,
};

Serializer.addProperty('survey', {
  name: 'enableFlagForMaintenanceOption',
  category: 'general',
  type: 'boolean',
  visibleIndex: 3,
});

Serializer.addProperty('question', {
  name: 'guid',
  category: 'general',
  type: 'string',
  readOnly: true,
  visibleIndex: 1,
});

ComponentCollection.Instance.add({
  name: 'propertyState',
  title: 'Property State',
  onCreated: question => {
    question.isRequired = true;
    question.title = 'Property state';
    question.name = question.title;
  },
  onAfterRender(question: Question, htmlElement: HTMLElement) {
    const container = htmlElement.querySelector('.sd-selectbase') as HTMLElement;
    if (container) {
      container.style.display = 'flex';
      container.style.gap = '32px';
    }
  },
  questionJSON: {
    type: 'radiogroup',
    choices: [
      {
        value: 'poor',
        text: 'Poor',
      },
      {
        value: 'fair',
        text: 'Fair',
      },
      {
        value: 'good',
        text: 'Good',
      },
    ],
  },
});

function isStartFromGuid(name: string) {
  return /[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}/.test(name.substring(0, 36));
}

@Directive()
export abstract class CommonSurveyCreatorComponent implements OnInit, OnDestroy {
  surveyCreatorModel: SurveyCreatorModel;

  protected abstract saveActionLogic(templateJson: string, title: string): void;
  protected abstract discardActionLogic(): void;
  protected abstract loadSurveyJson(): void;

  saveAction = new Action({
    id: 'save-changes',
    iconName: 'icon-checked',
    title: 'Save changes',
    showTitle: false,
    enabled: true,
    visibleIndex: 0,
    action: () => {
      const surveyJson = this.surveyCreatorModel.getSurveyJSON();
      const template = JSON.stringify(surveyJson);
      const title = surveyJson.title;
      this.saveActionLogic(template, title);
    },
  });

  discardAction = new Action({
    id: 'discard-changes',
    iconName: 'icon-clear_16x16',
    title: 'Discard changes',
    showTitle: false,
    enabled: true,
    visibleIndex: 1,
    action: () => this.discardActionLogic(),
  });

  constructor(protected cdr: ChangeDetectorRef) {}

  ngOnInit() {
    const creator = new SurveyCreatorModel(creatorOptions);
    creator.toolbarItems.push(this.saveAction);
    creator.toolbarItems.push(this.discardAction);
    creator.onShowingProperty.add(function (sender, options) {
      options.canShow = allowedProps.includes(options.property.name);
    });
    creator.onQuestionAdded.add((sender, options) => {
      const question = options.question;
      question.guid = uuidv4();
      if (!isStartFromGuid(question.name)) {
        question.title = question.name;
        question.name = uuidv4() + question.name;
      }

      question.onPropertyChanged.add((propertySender, propertyOptions) => {
        if (propertyOptions.name === 'title') {
          question.name = propertyOptions.newValue;
          if (isStartFromGuid(question.name)) {
            question.name = question.name.substring(0, 36) + propertyOptions.newValue;
          } else {
            question.name = uuidv4() + propertyOptions.newValue;
          }
        }
      });

      question.onItemValuePropertyChanged.add((sender, { obj, propertyName, name, newValue }) => {
        if (propertyName === 'choices' && name === 'text') {
          obj.value = newValue;
        }
      });

      if (question.customQuestion?.name === 'propertystate') {
        const questions = creator.survey.getAllQuestions().filter(q => q instanceof QuestionCustomModel);
        if (questions.length > 1) {
          question.title += questions.length - 1;
        }

        question.name = question.title;

        const panel = question.page.addNewPanel();
        panel.visibleIf = `{${question.name}} = 'poor' or {${question.name}} = 'fair'`;
        panel.requiredIf = `{${question.name}} = 'poor' or {${question.name}} = 'fair'`;
        panel.addNewQuestion('boolean');
        panel.addNewQuestion('boolean');
        panel.addNewQuestion('text');
      }

      if (question.parent instanceof PanelModel) {
        question.requiredIf = question.parent.requiredIf;
      }
    });
    creator.JSON = {
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
        },
      ],
    };
    this.surveyCreatorModel = creator;

    this.loadSurveyJson();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {}
}
